// GENERATED by `pnpm run gen:narrative` from src/core/content/narrative/requirements.md — DO NOT EDIT.
// Hand edits here are drift by definition: edit the markdown source and regenerate
// (the gen-narrative verify gate rebuilds this file and byte-compares it).
// Data only — the types + helpers stay hand-written in requirements.ts, which re-exports this.

import type { RequirementDef } from '../requirements-engine';
import type { RankId } from './ranks';
import { NAMES } from './names';

export const RUNG_REQUIREMENTS: Readonly<Record<RankId, readonly RequirementDef[]>> = {
  R0: [
    {
      id: 'rake-the-first-rows',
      type: 'count',
      token: 'act:rake_rice',
      target: 10,
      flavor: `"So he can work," ${NAMES.elder} says, in the way another man says good morning.`,
      drive: 'rake_rice',
    },
    {
      id: 'rake-through-the-night',
      type: 'count',
      token: 'act:rake_rice',
      target: 20,
      flavor: `"Still at it," ${NAMES.elder} says, passing the granary door without stopping.`,
      drive: 'rake_rice',
    },
    {
      id: 'rake-the-spill',
      type: 'count',
      token: 'act:rake_rice',
      target: 35,
      flavor: `"Dawn, and he is still raking," ${NAMES.elder} says to the dark, as if the dark had asked.`,
      drive: 'rake_rice',
    },
  ],

  R1: [
    {
      id: 'first-rows-of-his-own',
      type: 'count',
      token: 'act:farm_paddy',
      target: 10,
      flavor: "The first rows he plants alone come up as straight as anyone's.",
      drive: 'farm_paddy',
    },
    {
      id: 'take-to-the-fields',
      type: 'count',
      token: 'act:farm_paddy',
      target: 30,
      flavor: 'The paddy women no longer point out which rows are his; he has learned them.',
      drive: 'farm_paddy',
    },
    {
      id: 'a-porters-back',
      type: 'count',
      token: 'act:haul_stores',
      target: 25,
      flavor:
        'At the gate the porters shift a strap for him without being asked, as they do among their own.',
      drive: 'haul_stores',
    },
  ],

  R2: [
    {
      id: 'learn-the-woodlot',
      type: 'count',
      token: 'act:woodcut_edge',
      target: 20,
      flavor:
        'The axe haft darkens where his grip has settled, and the stumps at the woodlot edge stand cut clean.',
      drive: 'woodcut_edge',
    },
    {
      id: 'walk-the-hills',
      type: 'count',
      token: 'act:forage_satoyama',
      target: 25,
      flavor:
        'The near hills give up bracken and mushrooms without a search; the basket comes home heavy before noon.',
      drive: 'forage_satoyama',
    },
    {
      id: 'keep-the-stores-moving',
      type: 'count',
      token: 'act:haul_stores',
      target: 20,
      flavor:
        'Bales pass the gate and none sit long; the storehouse floor shows bare wood where sacks once rotted.',
      drive: 'haul_stores',
    },
  ],

  R3: [
    {
      id: 'clear-the-kura-rats',
      type: 'count',
      token: 'kill:river_rats',
      target: 3,
      flavor: `"Quiet in the kura tonight," ${NAMES.drillmaster} lets drop, cleaning his blade by lamplight.`,
      drive: 'fight river_rats',
    },
    {
      id: 'drive-off-the-thieves',
      type: 'count',
      token: 'kill:tanuki',
      target: 2,
      flavor: `"The monkeys have learned his shape," ${NAMES.drillmaster} is heard to say, almost pleased.`,
      drive: 'fight tanuki',
    },
    {
      id: 'the-fields-hold',
      type: 'count',
      token: 'act:farm_paddy',
      target: 15,
      flavor: `"The fields don't miss him while he drills," ${NAMES.elder} notes, half to himself.`,
      drive: 'farm_paddy',
    },
    {
      id: 'keep-the-rows',
      type: 'count',
      token: 'act:farm_paddy',
      target: 30,
      flavor: `"Watch or no watch, his rows never fell behind," ${NAMES.elder} says over the evening rice.`,
      drive: 'farm_paddy',
    },
    {
      id: 'timber-for-the-palisade',
      type: 'count',
      token: 'act:woodcut_edge',
      target: 25,
      flavor: `"The palisade wood is his cutting," ${NAMES.drillmaster} says, striking a post with the flat of his hand.`,
      drive: 'woodcut_edge',
    },
    {
      id: 'the-wolf-survived-not-won',
      type: 'flag',
      flag: 'wolf-survived-not-won',
      flavor:
        'The night the wolf came you stood between it and the stores, and were still there at dawn — bleeding, ribs cracked, the rice untouched. Not won. Survived.',
      drive:
        'the R3 first night round resolves its scripted survive-not-won wolf stage, setting `wolf-survived-not-won`.',
    },
  ],

  R4: [
    {
      id: 'a-load-among-loads',
      type: 'count',
      token: 'act:haul_stores',
      target: 25,
      flavor: 'The porters have stopped counting his loads against their own.',
      drive: 'haul_stores',
    },
    {
      id: 'first-name-in-the-yard',
      type: 'count',
      token: 'act:haul_stores',
      target: 50,
      flavor: 'When the kura needs moving in a hurry, it is his name the yard calls first.',
      drive: 'haul_stores',
    },
    {
      id: 'the-stores-know-you',
      type: 'count',
      token: 'act:haul_stores',
      target: 75,
      flavor: 'New porters are told to watch how he stacks; nobody remembers deciding that.',
      drive: 'haul_stores',
    },
    {
      id: 'a-hundred-mon-held',
      type: 'state',
      pred: { kind: 'resource', res: 'coin', min: 100 },
      flavor:
        'The house learns he keeps coin of his own, strung and counted, spent on nothing foolish.',
      drive: 'sell rice',
    },
  ],

  R5: [
    {
      id: 'first-deep-baskets',
      type: 'count',
      token: 'act:forage_deepwoods',
      target: 10,
      flavor:
        'The first deep-hill baskets come back full, and no one mentions the danger line twice.',
      drive: 'forage_deepwoods',
    },
    {
      id: 'past-the-danger-line',
      type: 'count',
      token: 'act:forage_deepwoods',
      target: 25,
      flavor:
        "He comes back from past the charcoal-burners' marker so often the kitchen stops waiting up.",
      drive: 'forage_deepwoods',
    },
    {
      id: 'the-paddies-still-first',
      type: 'count',
      token: 'act:farm_paddy',
      target: 25,
      flavor: `Whatever else he is now, the paddies get his mornings first, and ${NAMES.elder} sees that they do.`,
      drive: 'farm_paddy',
    },
    {
      id: 'a-made-corner',
      type: 'state',
      pred: { kind: 'belonging', id: 'bedding' },
      flavor:
        'There is bedding in the north corner, bought with his own coin, and the maids sweep around it now.',
      drive: 'buy bedding',
    },
  ],

  R6: [
    {
      id: 'the-count-comes-early',
      type: 'count',
      token: 'act:haul_stores',
      target: 25,
      flavor: `"The store-room count is early this month," ${NAMES.steward} says, not yet naming why.`,
      drive: 'haul_stores',
    },
    {
      id: 'the-ledgers-agree',
      type: 'count',
      token: 'act:haul_stores',
      target: 65,
      flavor: `"The ledgers agree tonight," ${NAMES.steward} says, which is her whole praise.`,
      drive: 'haul_stores',
    },
    {
      id: 'three-hundred-mon-steady',
      type: 'state',
      pred: { kind: 'resource', res: 'coin', min: 300 },
      flavor: `"His purse holds steady," ${NAMES.steward} lets fall, "which is more than this house has managed."`,
      drive: 'sell rice',
    },
    {
      id: 'coin-into-the-works',
      type: 'state',
      pred: { kind: 'native', key: 'estate-u1' },
      flavor: `"He put his own coin into the kura-works," ${NAMES.steward} tells ${NAMES.elder}, twice, to be sure he heard.`,
      drive: 'buy estate_upgrade',
    },
  ],

  R7: [
    {
      id: 'the-fields-run-on-you',
      type: 'count',
      token: 'act:farm_paddy',
      target: 20,
      flavor: `"The fields keep a man's hours now, and not mine — if mine ever held them," comes ${NAMES.lord}'s voice through the shoji of the inner house, unanswered; the yard knows whose hours he means.`,
      drive: 'farm_paddy',
    },
    {
      id: 'the-woodlot-runs-on-you',
      type: 'count',
      token: 'act:woodcut_edge',
      target: 20,
      flavor: `"Even the woodlot answers to him," ${NAMES.drillmaster} says to ${NAMES.elder}, who does not disagree.`,
      drive: 'woodcut_edge',
    },
    {
      id: 'a-granary-against-winter',
      type: 'state',
      pred: { kind: 'banked', res: 'rice', min: 200 },
      flavor: `"There is rice against winter. There was a saying for that — I had it once," ${NAMES.lord} is heard saying to the dark of the inner house, and nobody who hears it goes in.`,
      drive: 'deposit rice',
    },
    {
      id: 'the-nengu-reckoned',
      type: 'flag',
      flag: 'nengu-reckoned',
      flavor:
        'You stood in the room while the year was measured against the house — the reckoning read out, the gap named plainly and once, and nothing asked of you but to be there for it. A full year, lived through.',
      drive: 'sim reaches the Autumn season-exit, whose nengu ceremony sets `nengu-reckoned`.',
    },
  ],
};
